import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import ExpandableContentOrg from "@onestore/hel/dist/components/organisms/ExpandableContentOrg"
import GridOrg from "@onestore/hel/dist/components/organisms/GridOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import _t from "@core/i18n"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import BaseMarkdown from "@gatsby-plugin-generic-page/components/Markdown/Base"
import type {
  SeoContentSectionFlatData,
  SeoParagraph,
} from "@gatsby-plugin-generic-page/fragments/seoContentSection"
import { chunk } from "lodash"
import type { Components } from "react-markdown"
import { sendContentShowEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { sectionParagraphText } from "../markdown/sectionParagraphText"

type ColumnProps = {
  column: SeoParagraph[]
  isHeadingSafe?: boolean
}

const nonHeadingMarkdownOverrides: Components = {
  ...sectionParagraphText,
  h1: "span",
  h2: "span",
  h3: "span",
}

function Column({
  column,
  isHeadingSafe = true,
}: ColumnProps): ReactElement<ColumnProps> {
  return (
    <div>
      {column.map(({ title, text }) => (
        <PushOrg bottomSpace={4} key={title}>
          <TextAtm typography="medium3_h">{title}</TextAtm>

          <TextAtm typography="medium1_h" htmlTag="div">
            <BaseMarkdown
              components={
                isHeadingSafe
                  ? sectionParagraphText
                  : nonHeadingMarkdownOverrides
              }
            >
              {text}
            </BaseMarkdown>
          </TextAtm>
        </PushOrg>
      ))}
    </div>
  )
}

export default function SeoContentSection({
  header,
  paragraphs,
  sectionSettings,
}: SeoContentSectionFlatData): ReactElement<SeoContentSectionFlatData> {
  const renderContent = (isHeadingSafe: boolean) => (
    <GridOrg
      columns={{ small: 1, medium: 1, large: 2 }}
      gutterSpace={{ small: 3, medium: 3, large: { x: 10, y: 3 } }}
    >
      {chunk(paragraphs, Math.ceil(paragraphs.length / 2)).map(
        (column, index) => (
          <Column column={column} isHeadingSafe={isHeadingSafe} key={index} />
        )
      )}
    </GridOrg>
  )

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isEmpty(header) ? (
          <PushOrg bottomSpace={{ small: 4, medium: 5, large: 5 }}>
            <Header {...header} titleTypography="large2" />
          </PushOrg>
        ) : null}

        <BreakpointVisibilityOrg from="small" to="large">
          <ExpandableContentOrg
            showMoreDetails={{
              text: _t("showMore"),
              title: _t("showMore"),
              onClick: sendContentShowEvent,
            }}
            heightBreakpoint="short"
            breakpoint={{ from: "small", to: "medium" }}
          >
            {renderContent(true)}
          </ExpandableContentOrg>
        </BreakpointVisibilityOrg>

        <BreakpointVisibilityOrg from="large">
          {renderContent(false)}
        </BreakpointVisibilityOrg>
      </BaseContainer>
    </BaseSection>
  )
}
